import { useEffect, useId, useRef, useState } from 'react';
import type { Profile } from '../../../objectives/core/profile';
import { decideUpload } from '../../../objectives/core/upload';
import { ARRIVAL_SOURCE } from '../../core/events';
import { compareExpected, type ExpectedStandard } from '../../core/expected-standards';
import type { StandardsReport } from '../../core/standards-check';
import {
  CONTEXT_FIELD_METADATA, CONTEXT_FIELDS, resolveContext, type ContextField, type ContextSource, type StandardsContext,
} from '../../core/standards-context';
import { call, clearStandardsOverride, type GetToken, type MaterialSummary } from '../api';
import type { PlannerSession } from '../planner-handoff';

const errors: Record<string, string> = {
  checker_unavailable: 'Standards search is not configured. Ask the Collab administrator to enable its provider connection.',
  standards_check_disabled: 'Standards check is switched off right now. The document and its comments still work.',
  extraction_unavailable: 'This file format cannot be read on this deployment yet. Ask the administrator to configure document conversion.',
  source_missing: 'The uploaded file could not be found. Upload the material again.',
  extraction_empty: 'No readable text was found in this file. Try a text-based version.',
  extraction_failed: 'We could not read this file. Try a text-based version.',
  standards_unavailable: 'The standards source is unavailable. No findings were produced. Try again later.',
  check_limit: 'A check is already running for this material, or your weekly check limit has been reached. Try later.',
  invalid_context: 'Choose a state, subject and grade, and confirm this is teacher-authored material.',
};
export function safeSourceUrl(value?: string): string | null {
  if (!value) return null;
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; }
  catch { return null; }
}

/** The group default and this material's override, as the Worker holds them now. */
export interface StandardsSnapshot {
  standardsDefaults: StandardsContext | null;
  standardsOverride: StandardsContext | null;
}

type Values = Record<ContextField, string>;

const SOURCE_LABEL: Record<ContextSource, string | null> = {
  default: 'Group default',
  override: 'Set for this material',
  planner: 'From your Planner',
  teacher: 'Your default',
  none: null,
};

/** Each field's empty-option text. */
const PLACEHOLDER: Record<ContextField, string> = {
  jurisdiction: 'Choose state',
  academicSubject: 'Choose subject',
  grade: 'Choose grade',
};

/** A status that means the member can no longer reach this material. */
const accessLost = (status: number): boolean => [401, 403, 404].includes(status);

/** The form's values: what the member edited stays, every other field takes the resolved value. */
function valuesFrom(context: StandardsContext, keep: Partial<Values> = {}): Values {
  const values = {} as Values;
  for (const field of CONTEXT_FIELDS) values[field] = keep[field] ?? context[field] ?? '';
  return values;
}

/**
 * The Standards check on one material.
 *
 * The state, subject and grade start from the material's override, else the group default
 * (R5), and each says which (R9). The Worker saves the override when a run's values differ,
 * so this form never decides what is saved: before a run it re-reads the server and
 * re-resolves every field the member did not touch, so a default changed in another tab is
 * not pinned, and after a run it reads the saved override back rather than guessing whether
 * the run got far enough to write it.
 *
 * When it opens it also reads the teacher's own shared default, which fills any field the
 * override and the group leave empty, labelled "Your default" (U5 R9). Until it answers, or
 * when it fails, the form is exactly the two-layer form (AE7).
 *
 * A Planner hand-off is one more resolved layer, "From your Planner", between the group default
 * and the teacher's own (KTD5). It is passed to every `resolveContext` here, not copied into
 * the form once, so the re-read before and after a run keeps it. Its expected standards are
 * named above the form, and a finished check marks which ones the material covered. With no
 * hand-off the form, the request and the results are exactly what they were.
 */
export default function StandardsCheck({
  material, getToken, onAccessLost, standardsDefaults, standardsOverride, reloadContext, loadTeacherDefault, planner = null,
}: {
  material: MaterialSummary; getToken: GetToken; onAccessLost: () => void;
  standardsDefaults: StandardsContext | null; standardsOverride: StandardsContext | null;
  /** Re-reads the group and the material. Null when that failed. */
  reloadContext: () => Promise<StandardsSnapshot | null>;
  /** Reads the teacher's shared default, already one grade. Null when there is none or it failed. */
  loadTeacherDefault?: () => Promise<StandardsContext | null>;
  /** The Planner's hand-off held for this tab, if any. */
  planner?: PlannerSession | null;
}) {
  const plannerContext = planner?.handoff.context ?? null;
  const expected: readonly ExpectedStandard[] = planner?.handoff.expected ?? [];
  const [snapshot, setSnapshot] = useState<StandardsSnapshot>({ standardsDefaults, standardsOverride });
  const [teacher, setTeacher] = useState<StandardsContext | null>(null);
  const [values, setValues] = useState<Values>(() => valuesFrom(resolveContext(standardsOverride, standardsDefaults, null, plannerContext).context));
  /** The fields the member chose themselves. Only these survive a re-read. */
  const [edited, setEdited] = useState<ReadonlySet<ContextField>>(new Set());
  const [clearing, setClearing] = useState(false);
  const formId = useId();
  const [confirmed, setConfirmed] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<StandardsReport | null>(null);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => { controller.current?.abort(); }, []);
  // The teacher default fills only a field that is still empty, so a value the member chose
  // while it was loading is never overwritten (R9).
  useEffect(() => {
    if (!loadTeacherDefault) return;
    let live = true;
    void loadTeacherDefault().catch(() => null).then((fresh) => {
      if (!live || !fresh) return;
      setTeacher(fresh);
      setValues((current) => {
        const next = { ...current };
        for (const field of CONTEXT_FIELDS) if (next[field] === '' && fresh[field]) next[field] = fresh[field];
        return next;
      });
    });
    return () => { live = false; };
  }, [loadTeacherDefault]);
  const supported = decideUpload({ filename: '', contentType: material.contentType, byteLength: material.sizeBytes }).ok;

  const resolved = resolveContext(snapshot.standardsOverride, snapshot.standardsDefaults, teacher, plannerContext);
  const hasOverride = CONTEXT_FIELDS.some((field) => resolved.source[field] === 'override');
  const complete = CONTEXT_FIELDS.every((field) => values[field] !== '');

  /** Takes the server's answer, keeping only the values in `keep`. */
  function adopt(fresh: StandardsSnapshot, keep: Partial<Values>) {
    const { context } = resolveContext(fresh.standardsOverride, fresh.standardsDefaults, teacher, plannerContext);
    const next = valuesFrom(context, keep);
    setSnapshot(fresh);
    setValues(next);
    setEdited(new Set(CONTEXT_FIELDS.filter((field) => next[field] !== (context[field] ?? ''))));
    return next;
  }

  function choose(field: ContextField, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    setEdited((current) => new Set(current).add(field));
  }

  async function revertToGroupDefault() {
    setClearing(true); setError(null);
    const result = await clearStandardsOverride(getToken, material.id);
    if (!result.ok) {
      setClearing(false);
      if (accessLost(result.status)) { onAccessLost(); return; }
      setError('We could not switch this material back to the group default. Try again in a moment.');
      return;
    }
    const fresh = await reloadContext();
    setClearing(false);
    adopt(fresh ?? { ...snapshot, standardsOverride: null }, {});
  }

  async function run() {
    if (controller.current) return;
    const active = new AbortController();
    controller.current = active;
    setRunning(true); setError(null); setReport(null);
    const finish = () => { controller.current = null; setRunning(false); };

    // Re-read first, so a field nobody touched runs with the default as it is now (R6).
    const before = await reloadContext();
    if (active.signal.aborted) return;
    if (before === null) {
      finish();
      setError('The check could not finish. Try again in a moment.');
      return;
    }
    const kept: Partial<Values> = {};
    for (const field of edited) kept[field] = values[field];
    const ran = adopt(before, kept);
    if (CONTEXT_FIELDS.some((field) => ran[field] === '')) {
      finish();
      setError(errors.invalid_context ?? null);
      return;
    }

    // The first check after a Planner hand-off says so, once (KTD7).
    const fromPlanner = planner?.takeReport('check') ?? false;
    const result = await call<{ report: StandardsReport }>(getToken,
      `/api/materials/${encodeURIComponent(material.id)}/standards-check`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, signal: active.signal,
        body: JSON.stringify({
          jurisdiction: ran.jurisdiction, academicSubject: ran.academicSubject, gradeLevels: [ran.grade], teacherMaterial: confirmed,
          ...(fromPlanner ? { from: ARRIVAL_SOURCE } : {}),
        }),
      });
    if (active.signal.aborted) return;
    if (!result.ok && accessLost(result.status)) { finish(); onAccessLost(); return; }

    // Whether the override was saved depends on where the run stopped, so read it back
    // rather than infer it. A 400 stopped before anything was written.
    if (result.ok || result.status !== 400) {
      const after = await reloadContext();
      if (active.signal.aborted) return;
      if (after) adopt(after, ran);
    }
    finish();
    if (!result.ok) setError(errors[result.error] ?? 'The check could not finish. Try again in a moment.');
    else setReport(result.data.report);
  }

  return <section className="panel standards-check ph-no-capture ph-mask" data-ph-no-capture aria-label="Standards check" aria-busy={running}>
    <h3>Standards check</h3>
    <p>Check how this teaching material aligns with academic standards using Standard Checker. This is not a letter grade or an assessment of students. Review every match yourself.</p>
    <p className="hint">PDF, DOCX, text, Markdown and supported images, up to 8 MB. PowerPoint is not supported; export it to PDF. Results stay on this page and are not saved; the state, subject and grade are.</p>
    {expected.length > 0 && <p className="hint planner-expected">Expected from your Planner: {expected.map((s) => s.code).join(', ')}</p>}
    {!supported ? <p role="alert">This material is not a supported type or is over 8 MB. Upload a supported version to check it.</p> : <form onSubmit={(event) => { event.preventDefault(); void run(); }}>
      <fieldset disabled={running || clearing}>
        <legend>Standards context</legend>
        {CONTEXT_FIELD_METADATA.map(({ field, label, options }) => {
          // A label only while the value is the one it describes; an edit not yet run is neither.
          const source = values[field] === (resolved.context[field] ?? '') ? SOURCE_LABEL[resolved.source[field]] : null;
          const sourceId = `${formId}-${field}-source`;
          return <div className="standards-field" key={field}>
            <label>{label}<select required value={values[field]} onChange={(e) => choose(field, e.target.value)}
              {...(source ? { 'aria-describedby': sourceId } : {})}>
              <option value="">{PLACEHOLDER[field]}</option>{options.map((value) => <option key={value}>{value}</option>)}
            </select></label>
            {source && <span id={sourceId} className="hint">{source}</span>}
          </div>;
        })}
        {hasOverride && <button type="button" className="secondary" disabled={clearing} onClick={() => void revertToGroupDefault()}>Use group default</button>}
        <label className="standards-confirm"><input type="checkbox" required checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          This is teacher-authored instructional material, not student work or student records. I agree to send it to the checker’s processing providers.
        </label>
        <button type="submit" disabled={!complete || !confirmed || clearing}>{error ? 'Retry standards check' : report ? 'Run again' : 'Run standards check'}</button>
        <p className="hint">Running saves this state, subject and grade on this material for everyone in the group. They also become your default in other 240 Labs tools.</p>
      </fieldset>
    </form>}
    {running && <p role="status">Reading this material and checking standards… This may take up to two minutes.</p>}
    {error && <p role="alert" className="error">{error}</p>}
    {report && <Report report={report} expected={expected} />}
  </section>;
}

function contextLabel(profile: Profile) {
  return `${profile.jurisdiction} · ${profile.academicSubject} · Grade ${profile.gradeLevels.join(', ')}`;
}
const BAND_LABEL = { covered: 'Strong alignment', partial: 'Partial alignment', weak: 'Weak alignment' } as const;

/** The Planner's expected standards against what this check found. Absent with none expected. */
function ExpectedResults({ report, expected }: { report: StandardsReport; expected: readonly ExpectedStandard[] }) {
  const { found, notFound } = compareExpected(report.coverage.findings, expected);
  return <section className="planner-expected-results" aria-label="Expected standards">
    <h5>From your Planner</h5>
    {found.length > 0 && <>
      <p>Found in this material:</p>
      <ul aria-label="Expected standards found">
        {found.map((s) => <li key={s.id}><strong>{s.code}</strong> — {BAND_LABEL[s.band]}</li>)}
      </ul>
    </>}
    {notFound.length > 0 && <>
      <p>Not found in this material:</p>
      <ul aria-label="Expected standards not found">
        {notFound.map((s) => <li key={s.id}><strong>{s.code}</strong></li>)}
      </ul>
    </>}
  </section>;
}

function Report({ report, expected }: { report: StandardsReport; expected: readonly ExpectedStandard[] }) {
  return <div aria-label="Standards results">
    <h4>Alignment results</h4>
    <p>{contextLabel(report.profile)}</p>
    <p>{report.segmentCount} passages checked. Bands reflect semantic similarity, not proof of full coverage.</p>
    {report.objectivesProvider === 'heuristic' && <p className="hint">Passages were selected by the checker’s text segmenter, not an AI reading.</p>}
    {report.truncated && <p role="status">Only part of this material was checked because it exceeded the text or passage limit.</p>}
    {report.degradedSegments.length > 0 && <p role="status">Some standards searches failed. These results are incomplete; missing matches are not evidence of missing coverage.</p>}
    {expected.length > 0 && <ExpectedResults report={report} expected={expected} />}
    {report.coverage.findings.length === 0 && <p>No sufficiently strong matches were found for this context. Check the state, subject and grade before drawing conclusions.</p>}
    {report.coverage.findings.map((finding) => {
      const link = safeSourceUrl(finding.sourceUrl);
      return <article className="standards-finding" key={finding.caseIdentifierUUID}>
        <h4>{finding.statementCode} — {BAND_LABEL[finding.band]}</h4>
        <p>{finding.description}</p>
        <h5>Supporting passage</h5><blockquote>{finding.evidence}</blockquote>
        {link ? <a href={link} target="_blank" rel="noopener noreferrer">Read the standard at its source</a> : <p className="hint">Source link unavailable. Verify this standard with its issuing authority.</p>}
        {finding.author && <p>{finding.author}</p>}
        {finding.attributionStatement && <p className="hint">{finding.attributionStatement} {finding.license}</p>}
      </article>;
    })}
    <p className="hint">Standards search: Learning Commons. Teacher review required.</p>
  </div>;
}
